import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UtensilsCrossed } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const e = sp.get("error");
    if (e) setErr(decodeURIComponent(e));
  }, [sp]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const user = await login(email, password);
      const roleHome = { admin: "/dashboard", reception: "/tickets", tickets: "/scan", registration: "/tickets", bartender: "/scan" };
      nav(roleHome[user.role] || "/dashboard");
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] relative overflow-hidden flex items-center justify-center px-4">
      {/* Decorative accents */}
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-orange-500/20 blur-3xl" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-rose-600/20 blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-teal-500/10 blur-3xl" />

      <div className="relative z-10 w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center shadow-2xl shadow-orange-500/30 mb-4">
            <UtensilsCrossed className="text-white" size={32} />
          </div>
          <h1 className="font-display text-5xl font-black text-white tracking-tight">FOOD</h1>
          <h2 className="font-display text-5xl font-black text-orange-400 tracking-tight -mt-2">BAZAAR</h2>
          <p className="text-slate-400 text-sm mt-3 tracking-widest uppercase">Event Operations</p>
        </div>

        <form
          onSubmit={submit}
          className="bg-[#131D33] border border-slate-800 rounded-2xl p-8 shadow-2xl relative fb-grain"
          data-testid="login-form"
        >
          <h3 className="font-display text-2xl font-bold text-white mb-6 tracking-tight">SIGN IN</h3>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs tracking-widest uppercase text-slate-400">Email</Label>
              <Input
                id="email" type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 bg-[#0B1120] border-slate-700 text-white h-12"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs tracking-widest uppercase text-slate-400">Password</Label>
              <Input
                id="password" type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 bg-[#0B1120] border-slate-700 text-white h-12"
                data-testid="login-password"
              />
            </div>
          </div>

          {err && (
            <div className="mt-4 p-3 rounded-lg bg-rose-950 border border-rose-800 text-rose-300 text-sm" data-testid="login-error">
              {err}
            </div>
          )}

          <Button
            type="submit" disabled={loading}
            className="mt-6 w-full h-12 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold text-base rounded-xl shadow-lg shadow-orange-500/20"
            data-testid="login-submit"
          >
            {loading ? "Signing in…" : "SIGN IN"}
          </Button>

          <div className="mt-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-800" />
            <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-bold">Or</div>
            <div className="flex-1 h-px bg-slate-800" />
          </div>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <Button
            type="button"
            onClick={() => {
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="mt-4 w-full h-12 bg-white hover:bg-slate-100 text-slate-900 font-bold text-base rounded-xl border border-slate-300"
            data-testid="login-google-btn"
          >
            <svg className="mr-2 -ml-1" width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="mt-6 text-xs text-slate-500 text-center">
            Admin & Bar Staff · Food Bazaar Event Portal
          </div>
        </form>
      </div>
    </div>
  );
}
